import { z } from "zod";
import { DescriptionSchema, IdSchema, NameSchema, type Position } from "./primitives";

/** Instructions only the game master reads, such as a scenario's rules or its backstory. */
export const SkillSchema = z.strictObject({
  id: IdSchema,
  name: NameSchema,
  description: DescriptionSchema,
  body: z.string().max(1_000_000),
  read: z.enum(["always", "whenNeeded"]).optional().meta({
    description:
      "How the game master reads it: in full every time it wakes, the default, or by its name and description, opening the rest when it is needed.",
  }),
});
export type Skill = z.infer<typeof SkillSchema>;

export const SkillOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("skill.set"), skill: SkillSchema }),
  z.strictObject({
    type: z.literal("skill.update"),
    id: IdSchema,
    changes: z.strictObject({
      name: NameSchema.optional(),
      description: DescriptionSchema.optional(),
      body: SkillSchema.shape.body.optional(),
      read: SkillSchema.shape.read,
    }),
  }),
  z.strictObject({ type: z.literal("skill.unset"), id: IdSchema }),
]);
export type SkillOperation = z.infer<typeof SkillOperationSchema>;

/**
 * Something on the map a skill's text names: things by their ids, which the game master finds on
 * the map as it is when it reads them, or points, a line or an area as `[longitude, latitude]`
 * positions, copied off the map or drawn for the skill.
 */
export type SkillTag =
  | { kind: "things"; name: string; ids: string[] }
  | { kind: "points"; name: string; points: Position[] }
  | { kind: "line"; name: string; line: Position[] }
  | { kind: "area"; name: string; area: Position[][][] };

/** A stretch of a skill's text: words, or a tag, as written and as read, or null when it can't be read. */
export type SkillTextPart =
  | { kind: "words"; text: string }
  | { kind: "tag"; written: string; name: string; tag: SkillTag | null };

const TAG = /<(things|points|line|area)\b([^>]*)>([\s\S]*?)<\/\1>/g;
const NAME = /\bname="([^"]*)"/;

const escaped = (text: string) =>
  text.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");
const unescaped = (text: string) =>
  text.replaceAll("&lt;", "<").replaceAll("&quot;", '"').replaceAll("&amp;", "&");

const isPosition = (value: unknown): value is Position =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every(coordinate => typeof coordinate === "number" && Number.isFinite(coordinate));

const arePositions = (value: unknown, least: number): value is Position[] =>
  Array.isArray(value) && value.length >= least && value.every(isPosition);

function readTag(kind: SkillTag["kind"], name: string, inside: string): SkillTag | null {
  let value: unknown;
  try {
    value = JSON.parse(inside);
  } catch {
    return null;
  }
  switch (kind) {
    case "things":
      return Array.isArray(value) &&
        value.length > 0 &&
        value.every(id => typeof id === "string" && id.length > 0)
        ? { kind, name, ids: value }
        : null;
    case "points":
      return arePositions(value, 1) ? { kind, name, points: value } : null;
    case "line":
      return arePositions(value, 2) ? { kind, name, line: value } : null;
    case "area":
      return Array.isArray(value) &&
        value.length > 0 &&
        value.every(
          polygon =>
            Array.isArray(polygon) &&
            polygon.length > 0 &&
            polygon.every(ring => arePositions(ring, 4)),
        )
        ? { kind, name, area: value as Position[][][] }
        : null;
  }
}

/** A skill's text as words and the tags among them, in order. */
export function skillTextParts(body: string): SkillTextPart[] {
  const parts: SkillTextPart[] = [];
  let at = 0;
  for (const match of body.matchAll(TAG)) {
    const [written, kind, attributes, inside] = match as unknown as [
      string,
      SkillTag["kind"],
      string,
      string,
    ];
    if (match.index > at) parts.push({ kind: "words", text: body.slice(at, match.index) });
    const name = unescaped(NAME.exec(attributes)?.[1] ?? "");
    parts.push({ kind: "tag", written, name, tag: readTag(kind, name, inside) });
    at = match.index + written.length;
  }
  if (at < body.length) parts.push({ kind: "words", text: body.slice(at) });
  return parts;
}

/** A tag as a skill's text writes it. */
export function writtenSkillTag(tag: SkillTag): string {
  const inside =
    tag.kind === "things"
      ? tag.ids
      : tag.kind === "points"
        ? tag.points
        : tag.kind === "line"
          ? tag.line
          : tag.area;
  return `<${tag.kind} name="${escaped(tag.name)}">${JSON.stringify(inside)}</${tag.kind}>`;
}

/** A skill's words, each tag in them said as its name. */
export function skillWords(body: string): string {
  return skillTextParts(body)
    .map(part => (part.kind === "words" ? part.text : part.name))
    .join("");
}
